import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import LanguagePicker from './components/LanguagePicker';
import LandingPage from './components/landing/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import StaffPost from './pages/StaffPost';
import FamilyFeed from './pages/FamilyFeed';
import AdminPanel from './pages/AdminPanel';
import InstallPrompt from './components/InstallPrompt';
import DemoViewSwitcher from './components/DemoViewSwitcher';
import { apiFetch } from './api/client';

const STORAGE_KEY = 'kinness_session';

function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.user?.id || !parsed?.token) return null;
    return parsed;
  } catch {
    return null;
  }
}

function roleHome(role) {
  if (role === 'admin') return '/admin';
  if (role === 'staff') return '/staff/post';
  return '/family/feed';
}

function ProtectedRoute({ session, allowedRole, children }) {
  if (!session?.user) return <Navigate to="/login" replace />;
  if (allowedRole && session.user.role !== allowedRole) {
    return <Navigate to={roleHome(session.user.role)} replace />;
  }
  return children;
}

function AppRoutes() {
  const navigate = useNavigate();
  const [session, setSession] = useState(loadSession);
  const [showRegister, setShowRegister] = useState(false);

  const handleAuth = useCallback(
    (user, token, redirect) => {
      if (!user?.id || !token) {
        return;
      }
      const next = { user, token };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setSession(next);
      navigate(redirect || roleHome(user.role), { replace: true });
    },
    [navigate]
  );

  const handleLogout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  // Refresh profile so isDemo / role stay current after deploys (old sessions lack isDemo)
  useEffect(() => {
    const token = session?.token;
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch('/api/user/profile', { token });
        if (cancelled || !data?.user) return;
        setSession((prev) => {
          if (!prev?.token) return prev;
          const next = { ...prev, user: { ...prev.user, ...data.user } };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
          return next;
        });
      } catch {
        /* keep existing session */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.token]);

  const user = session?.user;
  const token = session?.token;

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/login"
          element={
            <Login
              onLogin={handleAuth}
              onSwitchToRegister={() => {
                setShowRegister(true);
                navigate('/register');
              }}
            />
          }
        />
        <Route
          path="/register"
          element={
            <Register
              onRegister={handleAuth}
              onSwitchToLogin={() => {
                setShowRegister(false);
                navigate('/login');
              }}
            />
          }
        />
        <Route
          path="*"
          element={
            showRegister ? (
              <Navigate to="/register" replace />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
      </Routes>
    );
  }

  return (
    <>
      {user?.isDemo ? (
        <DemoViewSwitcher currentRole={user.role} token={token} onSwitch={handleAuth} />
      ) : null}
      <Routes>
        <Route
          path="/staff/post"
          element={
            <ProtectedRoute session={session} allowedRole="staff">
              <StaffPost user={user} token={token} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/family/feed"
          element={
            <ProtectedRoute session={session} allowedRole="family">
              <FamilyFeed user={user} token={token} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute session={session} allowedRole="admin">
              <AdminPanel user={user} token={token} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route path="/login" element={<Navigate to={roleHome(user.role)} replace />} />
        <Route path="*" element={<Navigate to={roleHome(user.role)} replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </LanguageProvider>
  );
}

function AppShell() {
  const { langChosen, showPicker, chooseLanguage, closeLanguagePicker } = useLanguage();
  const location = useLocation();
  const isPublicPage = location.pathname === '/' || location.pathname === '/login' || location.pathname === '/register';

  if (!langChosen && !isPublicPage) {
    return (
      <>
        <LanguagePicker onChoose={chooseLanguage} />
        <InstallPrompt />
      </>
    );
  }

  return (
    <>
      <InstallPrompt />
      <AppRoutes />
      {showPicker && !isPublicPage ? (
        <div className="fixed inset-0 z-[100]">
          <LanguagePicker onChoose={chooseLanguage} onClose={closeLanguagePicker} />
        </div>
      ) : null}
    </>
  );
}

export default App;
